import { useState } from "react";
import { PILLARS, getPillar } from "../../data/ybf/pillars";
import {
  DEEP_DIVES, CONVERGENCE_TONE, INDICATOR_TONE, NGO_BAR_COLOR, INSIGHT_BORDER,
} from "../../data/ybf/deepDives";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  MapPin, Users, Calendar, Phone, TrendingDown, ArrowRight,
  AlertTriangle, CheckCircle2, AlertCircle, GitCompareArrows,
} from "lucide-react";

const SEVERITY_ICON = {
  critical: AlertCircle,
  warning: AlertTriangle,
  convergence: CheckCircle2,
};
const SEVERITY_ICON_COLOR = {
  critical: "text-[#E2231A]",
  warning: "text-amber-500",
  convergence: "text-emerald-600",
};

export default function DeepDiveAnalysis() {
  const [pillarId, setPillarId] = useState(DEEP_DIVES[0].pillar);
  const dd = DEEP_DIVES.find((d) => d.pillar === pillarId) || DEEP_DIVES[0];
  const p = getPillar(dd.pillar);
  const conv = CONVERGENCE_TONE[dd.convergence.tone];

  const chartData = dd.indicators.map((i) => ({
    label: i.label,
    ngo: i.ngo,
    voice: i.voice,
    tone: i.tone,
  }));

  return (
    <div data-testid="deep-dive-section" className="space-y-5">
      {/* Focus-area tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200" data-testid="deep-dive-tabs">
        {PILLARS.map((pl) => {
          const has = DEEP_DIVES.some((d) => d.pillar === pl.id);
          const active = pl.id === pillarId;
          return (
            <button
              key={pl.id}
              type="button"
              disabled={!has}
              onClick={() => setPillarId(pl.id)}
              data-testid={`deep-dive-tab-${pl.id}`}
              className={`px-3 pb-2 -mb-px text-[12px] font-semibold transition-colors border-b-2 ${
                active
                  ? "border-[#E2231A] text-[#E2231A]"
                  : has
                  ? "border-transparent text-gray-500 hover:text-gray-800"
                  : "border-transparent text-gray-300 cursor-not-allowed"
              }`}
            >
              {pl.name}
            </button>
          );
        })}
      </div>

      {/* Program header + context chips */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="deep-dive-header">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <h2 className="text-lg font-bold text-gray-900">{dd.program}</h2>
        </div>
        <div className="flex flex-wrap gap-2 pt-3">
          <Chip icon={MapPin}>{dd.location}</Chip>
          <Chip icon={Users}>{dd.partner}</Chip>
          <Chip icon={Users}>{dd.beneficiaries.toLocaleString("en-IN")} beneficiaries</Chip>
          <Chip icon={Calendar}>{dd.round}</Chip>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard testid="metric-card-convergence" title="Signal convergence">
          <div className={`text-3xl font-bold mt-1 ${conv.text}`}>{dd.convergence.value}%</div>
          <span className={`mt-2 inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${conv.chip}`}>
            <GitCompareArrows size={11} /> {dd.convergence.status}
          </span>
        </MetricCard>

        <MetricCard testid="metric-card-ngo" title={dd.ngoHeadline.label}>
          <div className="text-3xl font-bold text-gray-900 mt-1">{dd.ngoHeadline.value}%</div>
          <span className="mt-2 text-[11px] text-gray-500">{dd.ngoHeadline.sub}</span>
        </MetricCard>

        <MetricCard testid="metric-card-voice-gap" title={dd.voiceHeadline.label}>
          <div className="text-3xl font-bold text-[#E2231A] mt-1">{dd.voiceHeadline.value}%</div>
          <span className="mt-2 inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-[#E2231A] border border-red-200">
            <TrendingDown size={11} /> {dd.voiceHeadline.gap} {dd.voiceHeadline.gapUnit}
          </span>
        </MetricCard>

        <MetricCard testid="metric-card-calls" title="Voice AI calls completed">
          <div className="text-3xl font-bold text-gray-900 mt-1 flex items-center gap-2">
            <Phone size={20} className="text-gray-400" /> {dd.calls.value}
          </div>
          <span className="mt-2 text-[11px] text-gray-500">{dd.calls.completionPct}% completion rate</span>
        </MetricCard>
      </div>

      {/* Main analysis: indicator comparison + signal chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Indicator comparison */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-1" data-testid="indicator-comparison">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 mb-1">Indicator Comparison</div>
          <p className="text-[11px] text-gray-500 mb-4">Partner-reported vs Voice AI signal</p>
          <div className="space-y-4">
            {dd.indicators.map((ind, idx) => {
              const tone = INDICATOR_TONE[ind.tone];
              return (
                <div key={idx} data-testid={`indicator-row-${idx}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-semibold text-gray-800">{ind.label}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tone.chip}`}>{ind.note}</span>
                  </div>
                  <Bars label="NGO" value={ind.ngo} color={NGO_BAR_COLOR} />
                  <div className="h-1" />
                  <Bars label="Voice AI" value={ind.voice} color={tone.voice} />
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-[10px] font-semibold text-gray-500">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: NGO_BAR_COLOR }} /> NGO reported</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#E2231A]" /> Voice AI signal</span>
          </div>
        </div>

        {/* Signal chart */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-2" data-testid="signal-chart">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 mb-1">Signal Chart</div>
          <p className="text-[11px] text-gray-500 mb-4">Grouped comparison across indicators (%)</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }} barGap={4} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F1F1" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} interval={0} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#9CA3AF" }} tickLine={false} axisLine={false} unit="%" />
              <Tooltip
                cursor={{ fill: "#F9FAFB" }}
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E5E7EB" }}
                formatter={(v, n) => [`${v}%`, n === "ngo" ? "NGO reported" : "Voice AI signal"]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "ngo" ? "NGO reported" : "Voice AI signal")} />
              <Bar dataKey="ngo" name="ngo" fill={NGO_BAR_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="voice" name="voice" radius={[3, 3, 0, 0]} maxBarSize={28}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={INDICATOR_TONE[d.tone].voice} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insight cards */}
      <div className="space-y-3" data-testid="deep-dive-insights">
        {dd.insights.map((ins, idx) => {
          const Icon = SEVERITY_ICON[ins.severity];
          return (
            <div
              key={idx}
              data-testid={`insight-card-${idx}`}
              className={`bg-white rounded-r-xl border border-gray-200 p-4 shadow-sm flex flex-col sm:flex-row justify-between items-start gap-4 ${INSIGHT_BORDER[ins.severity]}`}
            >
              <div className="flex gap-3">
                <Icon size={18} className={`${SEVERITY_ICON_COLOR[ins.severity]} flex-shrink-0 mt-0.5`} />
                <div>
                  <div className="font-bold text-gray-900 text-[14px]">{ins.title}</div>
                  <p className="text-[12.5px] text-gray-600 mt-1 leading-relaxed">{ins.body}</p>
                </div>
              </div>
              <button
                type="button"
                data-testid={`insight-explore-link-${idx}`}
                className="text-[#E2231A] text-[12px] font-semibold hover:underline flex-shrink-0 inline-flex items-center gap-1 whitespace-nowrap"
              >
                {ins.link} <ArrowRight size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-[11px] font-medium">
      <Icon size={12} className="text-gray-400" /> {children}
    </span>
  );
}

function MetricCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col shadow-sm">
      <span className="text-[11px] font-semibold text-gray-500">{title}</span>
      {children}
    </div>
  );
}

function Bars({ label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] font-semibold text-gray-400 w-12 flex-shrink-0">{label}</span>
      <div className="flex-1 h-3.5 bg-gray-100 rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="text-[11px] font-bold text-gray-700 w-9 text-right flex-shrink-0">{value}%</span>
    </div>
  );
}
